import { useAtom } from "jotai";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useErrorHandler } from "react-error-boundary";

import { updateFormioSubmission } from "../../../shared/api/yenta-api";
import { cplFormSubmissionErrorHandler } from "../../../shared/sentry/error-handler-utils";
import {
  EXTRA_ENTRIES_PER_SMS_ACCEPTANCE,
  QUIZ_QUESTION_CHANGE_ANSWER_LIMIT_SECONDS,
  QUIZ_QUESTION_CORRECT_ANSWER_DELAY_SECONDS,
} from "../../widget.constants";
import {
  currentPageAtom,
  currentQuestionIndexAtom,
  currentQuestionsAtom,
  paginatedQuestionsAtom,
  quizAtom,
  smsAtom,
} from "../../widget.state";
import { QuizType, QuizVariantEnum } from "../../widget.types";

export function QuizFooter() {
  const [quiz] = useAtom(quizAtom);
  const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
  const [paginatedQuestions] = useAtom(paginatedQuestionsAtom);
  const [currentQuestions] = useAtom(currentQuestionsAtom);
  const [currentQuestionIndex] = useAtom(currentQuestionIndexAtom);
  const [sms] = useAtom(smsAtom);
  const handleWidgetError = useErrorHandler();
  const [doublePoints, setDoublePoints] = useState<boolean>(false);
  const [autoNextPageDebounce, setAutoNextPageDebounce] = useState<
    _.DebouncedFunc<() => void>
  >();
  const [
    autoNextPageDebounceAllowed,
    setAutoNextPageDebounceAllowed,
  ] = useState<boolean>(false);
  const isArcade = quiz.isArcade;

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const totalEntries = useMemo(
    () =>
      quiz.questions.reduce(
        (acc, question) =>
          acc +
          (question.options.some((q) => q.isSelected && q.isCorrect) ? 1 : 0),
        quiz.allTimePoints ?? 0
      ),
    [quiz.allTimePoints, quiz.questions]
  );

  const canContinue = useMemo(() => {
    const [{ index: lastQuestionIndex }] = [...currentQuestions].reverse();
    return currentQuestionIndex > lastQuestionIndex;
  }, [currentQuestionIndex, currentQuestions]);

  const quizExtraEntries = useMemo(
    () =>
      quiz.questions.reduce(
        (acc, question) =>
          question.options.some(
            (option) => option.isSelected && option.isCorrect
          )
            ? acc + 1
            : acc,
        0
      ),
    [quiz.questions]
  );

  const moveToNextPage = () => {
    window.location.href = window.theme.Routing.redirectToNextPage(
      (window as any).FKL.collectionMetafields,
      (window as any).FKL.collectionHandle.replace("-quiz", ""),
      "quiz"!
    );
  };

  const handleSubmit = useCallback(async () => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();

    if (!submission) {
      console.error("No submission to update.");
      return;
    }

    try {
      const { recaptcha_token_refresh_rate = 5 } = window.FKL;
      const updatedSubmission =
        (currentPage + 1) % recaptcha_token_refresh_rate === 0
          ? await window.theme.RecaptchaUtils.updateSubmissionToken(submission)
          : submission;
      window.theme.Sweepstake.prototype.storeSubmission(updatedSubmission);
      await Promise.all(
        Array.from(
          new Set(
            currentQuestions
              .filter(
                (q) =>
                  q.cpl?.isAccepted &&
                  q.cpl.isTacAccepted &&
                  typeof q.cpl.formPath === "string"
              )
              .map((q) => q.cpl!.formPath!)
          )
        ).map((formPath) => {
          // @ts-ignore
          const baseURL: string = window.theme.QuizUtils.getBaseURLForCPLForm(
            formPath
          );
          const formio = new window.Formio(`${baseURL}/${formPath}`);

          return formio.saveSubmission({
            data: {
              email: submission?.data.email,
              agreeToTerms: true,
            },
          });
        })
      ).catch((err) => {
        cplFormSubmissionErrorHandler(err);
      });
    } finally {
      setCurrentPage((p) => {
        const nextPage = p + 1;
        if (paginatedQuestions[nextPage]) {
          window.scrollTo({
            top: 0,
            behavior: "smooth",
          });
          return nextPage;
        }

        if (quiz.isArcade) {
          window.scrollTo(0, 0);
          document.location.reload();
        } else if (window.FKL.shouldDoublePoints) {
          setDoublePoints(true);
        } else {
          moveToNextPage();
        }

        return p;
      });
    }
  }, [
    currentQuestions,
    paginatedQuestions,
    quiz.isArcade,
    currentPage,
    setCurrentPage,
  ]);

  useEffect(() => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    const performDoublePoints = async () => {
      if (submission) {
        const sweepFormUrl =
          document
            .querySelector("#FormSweepstake")
            ?.getAttribute("data-form-path-url") ?? "";
        try {
          await updateFormioSubmission(
            sweepFormUrl,
            {
              ...submission,
              should_double_points: true,
              sms_extra_entries_per_session:
                sms.selectedCompanies.length * EXTRA_ENTRIES_PER_SMS_ACCEPTANCE,
              extra_entries_per_session: quizExtraEntries,
            },
            handleWidgetError
          ).then((submission: any) => {
            window.theme.Sweepstake.prototype.storeSubmission(submission);
          });
        } finally {
          moveToNextPage();
        }
      }
    };
    if (doublePoints) {
      performDoublePoints();
      setDoublePoints(false);
    }
  }, [
    doublePoints,
    quizExtraEntries,
    sms.selectedCompanies.length,
    handleWidgetError,
  ]);

  const convertEntriesText = (entries: number): string =>
    entries === 1 ? `${entries} entry` : `${entries} entries`;

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const RenderContinue = ({
    entries,
    quiz,
  }: {
    entries: number;
    quiz: QuizType;
  }): JSX.Element =>
    !quiz.isArcade &&
    paginatedQuestions.length === currentPage + 1 &&
    window.FKL.showArcadePlayNowButton ? (
      <>
        <h1 className="footer__heading">
          Congrats, you earned{" "}
          <span className="bold">{convertEntriesText(entries)}</span>{" "}
          <img
            src={window.FKL.assetBonnetUrl}
            alt="bonnet"
            className="bonnet--quiz"
          />
        </h1>
        <p className="footer__paragraph">
          Continue to play: Earn points. Exchange points for prizes in our prize
          vault.
        </p>
        <PlayNowButton />
      </>
    ) : (
      <SubmitAndContinueButton />
    );

  const PlayNowButton = (): JSX.Element => (
    <div className="quiz__buttons__button-container">
      <button
        disabled={!canContinue}
        className="quiz__buttons__button quiz__buttons__button--submit"
        onClick={handleSubmit}
      >
        Play Now
      </button>
    </div>
  );

  const SubmitAndContinueButton = (): JSX.Element => (
    <button
      disabled={!canContinue}
      className="quiz--single-page__footer__submit-button"
      onClick={handleSubmit}
    >
      Submit &amp; Continue
    </button>
  );

  const RenderWarning = (): JSX.Element => (
    <div className="quiz__feedback quiz__feedback--incorrect">
      Please complete all the questions to advance to the next page
    </div>
  );

  const handleDebounceNextPage = useCallback(
    (waitTime: number) => {
      setAutoNextPageDebounce((prevDebounce) => {
        prevDebounce?.cancel();
        return debounce(handleSubmit, waitTime * 1000);
      });
    },
    [handleSubmit]
  );

  const waitAutoNextPage = useCallback(() => {
    const lastQuestion = currentQuestions[currentQuestions.length - 1];
    const isSingleQuestionLastQuestion = !(
      (lastQuestion.smsInfo?.brandName &&
        lastQuestion.smsInfo.question &&
        lastQuestion.smsInfo.smsBrandPrivacyPolicy &&
        lastQuestion.smsInfo.tacSmsBrand) ||
      lastQuestion.cpl?.title
    );
    const isLastQuestionAnswered = quiz.isArcade
      ? lastQuestion.options.some((option) => option.isSelected)
      : lastQuestion.options.some(
          (option) => option.isCorrect && option.isSelected
        );
    const isReminderOnLastQuestion: boolean =
      quiz.variant.toUpperCase() === QuizVariantEnum.Quiz &&
      window.FKL.allowOptInReminder &&
      (quiz.submissionCount || 1) > 1 &&
      currentPage === 0 &&
      window.FKL.questionsPerPage === 1;

    return isSingleQuestionLastQuestion &&
      !isLastQuestionAnswered &&
      !isReminderOnLastQuestion
      ? QUIZ_QUESTION_CHANGE_ANSWER_LIMIT_SECONDS
      : isSingleQuestionLastQuestion && !isReminderOnLastQuestion
      ? QUIZ_QUESTION_CORRECT_ANSWER_DELAY_SECONDS
      : 0;
  }, [
    currentQuestions,
    quiz.isArcade,
    currentPage,
    quiz.submissionCount,
    quiz.variant,
  ]);

  useEffect(() => {
    if (canContinue) {
      const waitAutoNextPageSeconds = waitAutoNextPage();
      if (waitAutoNextPageSeconds !== 0 || autoNextPageDebounceAllowed) {
        handleDebounceNextPage(waitAutoNextPageSeconds);
      }
    }
  }, [
    handleDebounceNextPage,
    canContinue,
    waitAutoNextPage,
    autoNextPageDebounceAllowed,
  ]);

  useEffect(() => {
    if (autoNextPageDebounce) {
      setAutoNextPageDebounceAllowed(false);
      autoNextPageDebounce();
    }
  }, [autoNextPageDebounce]);

  useEffect(() => {
    setAutoNextPageDebounceAllowed(true);
  }, [currentQuestionIndex]);

  return (
    <div className="quiz--single-page__footer">
      {(!canContinue || isArcade) && <RenderWarning />}
    </div>
  );
}
